import React from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Lightbulb, 
  Shield, 
  Leaf,
  Building2,
  Microscope,
  Truck,
  CheckCircle
} from 'lucide-react';
import { Breadcrumb } from '../components/Breadcrumb';

const values = [
  { title: 'Quality First', description: 'Uncompromising standards in every seed we produce', icon: Shield },
  { title: 'Innovation', description: 'Continuous research for better crop varieties', icon: Lightbulb },
  { title: 'Sustainability', description: 'Eco-friendly practices for future generations', icon: Leaf },
  { title: 'Integrity', description: 'Honest dealings with farmers and partners', icon: Heart },
];

const certifications = [
  'ISO 9001:2015 Certified',
  'ISTA Accredited Laboratory',
  'Organic Certification (NPOP)',
  'Good Agricultural Practices (GAP)',
  'Seed Certification Agency Approved',
  'ICAR Research Partnership',
];

const infrastructure = [
  { title: 'Processing Plants', description: '5 state-of-the-art seed processing facilities', icon: Building2 },
  { title: 'R&D Centers', description: '3 research centers with modern laboratories', icon: Microscope },
  { title: 'Distribution', description: 'Network of 200+ distributors nationwide', icon: Truck },
  { title: 'Quality Labs', description: 'In-house testing for 100% quality assurance', icon: CheckCircle },
];

export default function AboutPage() {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Breadcrumb items={[{ label: 'About Us' }]} />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-6">
              About Dream Field Agri solution
            </h1>
            <p className="text-white/90 text-lg leading-relaxed">
              Since 2006, we've been at the forefront of agricultural innovation, 
              providing farmers with premium seeds that yield bountiful harvests.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block text-primary font-semibold text-sm mb-4 uppercase tracking-wide">Our Story</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-6">
                A Legacy of Agricultural Excellence
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>
                  Dream Field Agri solution was founded in 2006 by a group of passionate agriculturists 
                  who believed that the foundation of a successful harvest lies in the quality of seeds. 
                  What started as a small seed processing unit has grown into one of the nation's leading 
                  seed companies.
                </p>
                <p>
                  Our journey began in a modest facility with just five employees. Today, we operate 
                  five state-of-the-art processing plants, three research centers, and serve over 
                  5,000 farmers across the country. Our success story is built on the trust of 
                  generations of farmers who have chosen Dreamfield for their seed needs.
                </p>
                <p>
                  Over the years, we've continuously invested in research and development, 
                  introducing new crop varieties that are disease-resistant, high-yielding, 
                  and adapted to local conditions. Our team of agronomists and researchers 
                  work tirelessly to ensure every seed meets our exacting standards.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1601601319316-bace8ae2b548?w=400"
                  alt="Modern greenhouse"
                  className="rounded-2xl shadow-medium w-full h-48 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=400"
                  alt="Wheat field"
                  className="rounded-2xl shadow-medium w-full h-64 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1592864554447-5e40d96e2b21?w=400"
                  alt="Farmer with seeds"
                  className="rounded-2xl shadow-medium w-full h-64 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400"
                  alt="Rice seeds"
                  className="rounded-2xl shadow-medium w-full h-48 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-10 shadow-soft"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-stone-900 mb-4">Our Mission</h3>
              <p className="text-stone-600 leading-relaxed">
                To empower farmers with high-quality seeds and agricultural solutions that 
                maximize yields, improve livelihoods, and contribute to food security. 
                We are committed to sustainable practices that protect our environment 
                while meeting the growing demands of agriculture.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-10 shadow-soft"
            >
              <div className="w-16 h-16 bg-secondary/20 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-heading font-bold text-2xl text-stone-900 mb-4">Our Vision</h3>
              <p className="text-stone-600 leading-relaxed">
                To be the most trusted name in the seed industry, recognized for innovation, 
                quality, and farmer-centric approach. We envision a future where every 
                farmer has access to superior seeds that unlock the full potential of 
                their land, ensuring prosperity for generations to come.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-semibold text-sm mb-4 uppercase tracking-wide">Our Values</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-4">
              What Drives Us Forward
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Our core values guide every decision we make and every seed we produce.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center p-8 rounded-2xl bg-stone-50 hover:bg-primary group transition-colors duration-300"
                >
                  <div className="w-16 h-16 bg-primary/10 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-colors duration-300">
                    <Icon className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-stone-900 group-hover:text-white mb-3 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-stone-600 group-hover:text-white/80 transition-colors duration-300">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure */}
      <section className="py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm mb-4 uppercase tracking-wide">Infrastructure</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              State-of-the-Art Facilities
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Our world-class infrastructure ensures the highest quality standards at every stage.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructure.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center"
                >
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-primary font-semibold text-sm mb-4 uppercase tracking-wide">Certifications</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-4">
              Quality Assurance & Certifications
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Our commitment to quality is validated by nationally and internationally recognized certifications.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center gap-4 bg-white rounded-xl p-6 shadow-soft"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <span className="font-medium text-stone-900">{cert}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
